import { DynamicForm, FormConfig } from "@components/DynamicForm/DynamicForm";
import { useNotification } from "@hooks/useNotification";
import { useQuery } from "@hooks/useQuery";
import { useDocumentDetails } from "@modules/ECMS/MAGP/ApprovalManagement/Drilldown.Hooks";
import { decodeHTML } from "@utils/CommonUtils";
import { useEffect } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { post, put } from "~/Services";
import { eramanHost } from "~/Utils";
import { fields } from "../constants";
import { useAdsPromotionDocumentDetails } from "./hooks/AdsBanner.hook";

export const AddEditAdsPromotion = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const { openToast } = useNotification();

  // editId flow (DB/master data)
  const {
    data: promotionData,
    isLoading: promotionLoading,
    refetch: refetchPromotion,
  } = useAdsPromotionDocumentDetails(query.get("editId") || "");

  // id flow (draft/submitted document details)
  const { data: promotionById, isLoading: docLoading, refetch: refetchDoc } = useDocumentDetails(query.get("id") || "");

  useEffect(() => {
    if (query.get("editId")) {
      refetchPromotion();
    } else {
      // no editId, still fetch normal banner promotion data
      refetchPromotion();
    }

    if (query.get("id")) {
      refetchDoc();
    }
  }, [query.get("editId"), query.get("id")]);

  const brandFromFields: FormConfig = {
    initialValues: {
      image: promotionById?.payload
        ? decodeHTML([
            {
              uid: "-1",
              name: "image.png",
              status: "done",
              url: promotionById?.payload?.image,
            },
          ])
        : decodeHTML([
            {
              uid: "-1",
              name: "image.png",
              status: "done",
              url: promotionData?.image,
            },
          ]),

      field_english_title: decodeHTML(
        promotionById?.payload?.field_english_title ?? promotionData?.field_english_title ?? ""
      ),
      field_malay_title: decodeHTML(
        promotionById?.payload?.field_malay_title ?? promotionData?.field_malay_title ?? ""
      ),
      field_mandarin_title: decodeHTML(
        promotionById?.payload?.field_mandarin_title ?? promotionData?.field_mandarin_title ?? ""
      ),
      // published: promotionById?.payload?.published ?? promotionData?.published ?? false,
    },
    fields: fields.bannerPromotion,
  };

  const onSubmit = async (
    data: FieldValues,
    apiUrl: string,
    action: string,
    method: string,
    status: "DRAFT" | "PENDING",
    reviewNotes?: string
  ) => {
    let imageKeys: any = ["image"];
    for (const key of imageKeys) {
      try {
        let field = data[key];
        if (field?.[0]?.originFileObj) {
          const formData = new FormData();
          formData.append("upload-file", field[0].originFileObj);
          formData.append("application", "ERAMAN");
          const res: any = await post("management/upload-file", formData);

          if (res) {
            data[key] = res.response.data;
          }
        } else if (field?.[0]?.url) {
          data[key] = field[0].url;
        } else {
          data[key] = ""; // fallback in case of invalid field
        }
      } catch (error) {
        console.error("Image processing failed for:", key, error);
      }
    }

    const payload = {
      action: action,
      method: method,
      module: "Advertisement",
      subModule: "bannerPromotion",
      type: "DEDICATED",
      application: "ERAMAN",
      reviewNotes,
      appList: [
        {
          appName: "ERAMAN",
          apiUrl,
        },
      ],
      payload: {
        ...(action === "UPDATE" && {
          id: promotionData?.id || promotionById?.payload?.id,
        }),
        ...data,
        status: true,
      },
      status,
    };

    console.log("Final Payload:", payload);

    if (query.get("id")) {
      // status-information flow
      put(`/management/update-document?approvalId=${query.get("id")}`, payload)
        .then((response: any) => {
          openToast({
            content: status == "PENDING" ? "Data Sent for Review" : "Data Saved Successfully",
            type: "success",
          });
          if (response.status === "OK" && status == "PENDING") {
            navigate("/dedicated/status-information?activeTab=pending");
          } else {
            navigate("/dedicated/status-information?activeTab=draft");
          }
        })
        .catch(() => {
          openToast({ content: "Failed to save data", type: "error" });
        });
    } else {
      // editId flow
      post("/management/publish", payload)
        .then((response: any) => {
          openToast({
            content: status == "DRAFT" ? "Data saved successfully" : "Data Sent for Review",
            type: "success",
          });
          if (response.status === "OK" && status == "PENDING") {
            navigate("/dedicated/status-information?activeTab=pending");
          } else {
            navigate("/dedicated/status-information?activeTab=draft");
          }
        })
        .catch(() => {
          openToast({ content: "Failed to save data", type: "error" });
        });
    }
  };

  return (
    <DynamicForm
      isLoading={promotionLoading || docLoading}
      config={brandFromFields}
      onSubmit={(data, buttonValue, reviewNotes) =>
        onSubmit(
          data,
          `${eramanHost}/api/prmotions-products-banner/${promotionData?.id || promotionById?.payload?.id}`,
          query.get("editId") ? "UPDATE" : "UPDATE",
          query.get("editId") ? "PATCH" : "PATCH",
          buttonValue == "save" ? "DRAFT" : "PENDING",
          reviewNotes
        )
      }
    />
  );
};
